import type { HubListHud } from "../game/hubs";
import { SECONDARY } from "./buttons";
import { KeyHint } from "./KeyHint";
import { buttonFor, type InputKind } from "./input-names";
import { DIALOG, ROW, ROW_ON, SCRIM } from "./theme";

/**
 * A challenge hub's list (#86): that tier's challenges, each with how to do
 * it, what it pays and a check once done. Pick one (X, a click or a tap) and
 * it starts at its own start spot; O or Start closes the list.
 */
export function HubList({
  hub,
  onPick,
  onHover,
  onClose,
  input = "keyboard",
}: {
  hub: HubListHud;
  onPick: (id: string) => void;
  onHover: (index: number) => void;
  onClose: () => void;
  /** The input you're using: its buttons are the ones named (#147). */
  input?: InputKind;
}) {
  return (
    <div className={`absolute inset-0 grid place-items-center ${SCRIM} p-4`}>
      <div
        role="dialog"
        aria-label={`${hub.name} challenges`}
        className={`${DIALOG} max-h-[calc(100dvh-2rem)] w-[min(92vw,30rem)] overflow-y-auto`}
      >
        <div className="text-center text-[11px] font-black uppercase tracking-widest opacity-70">Challenges</div>
        <h2 className="text-center text-2xl font-black uppercase italic">{hub.name}</h2>
        <ul className="mt-3 flex flex-col gap-2">
          {hub.items.map((item, i) => (
            <li key={item.id}>
              <button
                type="button"
                autoFocus={i === hub.index}
                aria-current={i === hub.index}
                onClick={() => onPick(item.id)}
                onPointerEnter={() => onHover(i)}
                className={`w-full rounded-xl border-2 px-3 py-2 text-center ${
                  i === hub.index ? ROW_ON : item.done ? "border-line bg-aqua-soft text-ink" : ROW
                } focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-focus`}
              >
                <span className="flex items-baseline justify-center gap-2">
                  <span className="text-base font-extrabold uppercase tracking-wide">
                    <span aria-hidden="true" className="mr-2 inline-block w-3">
                      {item.done ? "✓" : "·"}
                    </span>
                    {item.text}
                    <span className="sr-only">{item.done ? " (done)" : " (not done)"}</span>
                  </span>
                  {item.reward > 0 && (
                    <span
                      className="shrink-0 rounded-md border-2 border-ink bg-aqua-soft px-1.5 text-xs font-black tabular-nums text-ink"
                      aria-label={`pays ${item.reward} stat point${item.reward === 1 ? "" : "s"}`}
                    >
                      +{item.reward}
                    </span>
                  )}
                </span>
                <span className="mt-0.5 block text-xs font-semibold">{item.how}</span>
              </button>
            </li>
          ))}
        </ul>
        <button type="button" onClick={onClose} className={`${SECONDARY} mt-3 w-full px-4 py-2 text-lg`}>
          Back to skating <KeyHint input={input} button="○" />
        </button>
        <p className="mt-2 text-center text-xs font-semibold opacity-70">{input === "touch" ? "Tap one" : `${buttonFor(input, "✕")} picks one`}: it starts at its spot in the park</p>
      </div>
    </div>
  );
}
